import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon, InfinityIcon, TruckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { getLegalDoc, legalDocs, LegalSection } from '../data/legal';
import { media } from '../data/product';
import { SiteFooter } from '../components/SiteFooter';
import { PaymentLogos } from '../components/PaymentLogos';
const promises = [{
  icon: InfinityIcon,
  label: 'Break it. We replace it.'
}, {
  icon: RotateCcwIcon,
  label: '30-day returns, we pay shipping'
}, {
  icon: TruckIcon,
  label: 'Ships from the U.S. in 1 business day'
}, {
  icon: ShieldCheckIcon,
  label: 'Real person on support'
}];
export function Legal() {
  const {
    slug = ''
  } = useParams();
  const doc = getLegalDoc(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const renderSection = (section: LegalSection) => <section key={section.heading} className="border-t border-white/10 pt-7" data-id="element-1677" data-mp="3s605tcibykrw">
      <h2 className={section.fine ? 'font-display text-lg font-bold uppercase tracking-wide text-steel-400' : 'font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl'} data-id="element-1678" data-mp="27kd54pdvo948">
        {section.heading}
      </h2>
      <div className={`mt-3 grid max-w-3xl ${section.fine ? 'gap-2' : 'gap-3'}`} data-id="element-1679" data-mp="2cjy5934sanij">
        {section.body.map(paragraph => <p key={paragraph} className={section.fine ? 'text-[12px] leading-relaxed text-steel-500' : 'text-[15px] leading-relaxed text-steel-300 sm:text-base'} data-id="element-1680" data-mp="1ov6joulp93eq">
            {paragraph}
          </p>)}
      </div>
    </section>;
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1681" data-mp="1645mab089fxa">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950/95 backdrop-blur" data-id="element-1682" data-mp="1622lqx1g84eb">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8" data-id="element-1683" data-mp="112ydq9q9gcpk">
          <Link to="/" className="shrink-0" data-id="element-1684" data-mp="366exu32t9qic">
            <span className="block font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-offwhite sm:text-3xl" data-id="element-1685" data-mp="365dd16vztpnf">
              Grit<span className="text-grit-500" data-id="element-1686" data-mp="3azhitmtivrmx">Vue</span>
              <sup className="ml-0.5 align-super text-[9px] text-steel-400" data-id="element-1687" data-mp="3exno0qeppz5q">™</sup>
            </span>
            <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.3em] text-steel-400 sm:text-[9px]" data-id="element-1688" data-mp="2i9rdo39vc02y">
              See the job through
            </span>
          </Link>
          <Link to="/#offer" className="inline-flex shrink-0 items-center bg-grit-500 px-3.5 py-2.5 font-display text-base font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:px-4 sm:text-lg" data-id="element-1689" data-mp="2r82ytpryu5o0">
            Get your X1
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-id="element-1690" data-mp="llf5wm6odevh">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1691" data-mp="go56cdpknmtx">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1692" data-mp="7u9z904w3mbn" />
          Back to the X1
        </Link>

        {!doc ? <div className="mt-8" data-id="element-1693" data-mp="2t2tt7q7yz85">
            <h1 className="grit-headline text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1694" data-mp="3u1gxcndkjll3">
              Page not found
            </h1>
            <p className="mt-4 text-lg text-steel-300" data-id="element-1695" data-mp="3p4gqwbct4gtg">Pick one of our policies below.</p>
          </div> : <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16" data-id="element-1696" data-mp="1nb0oyib9rfj6">
            <article data-id="element-1697" data-mp="1n8xyxya49meg">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-1698" data-mp="1j9ypq1hcr3h7">{doc.eyebrow}</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-1699" data-mp="1qcdhzuxooe6o">
                {doc.title}
              </h1>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-steel-500" data-id="element-1700" data-mp="1v9m2412e6sak">
                Last updated {doc.updated}
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-200" data-id="element-1701" data-mp="1lchdzvozkmr4">{doc.intro}</p>

              <div className="mt-10 grid gap-8" data-id="element-1702" data-mp="17lm5wlmba9ol">
                {doc.sections.filter(section => !section.fine).map(renderSection)}
              </div>

              <div className="mt-12 grid gap-px border-2 border-grit-500 bg-white/10 lg:grid-cols-[1.1fr_1fr]" data-id="element-1703" data-mp="xohqd4mz1cgr">
                <img src={media.lensSheet} alt="GritVue X1 illuminated work glasses shown with its clear, amber and smoke lenses" className="block h-auto w-full bg-ink-950 object-contain" data-id="element-1704" data-mp="12mrnvlw64p5j" />
                <div className="flex flex-col justify-center bg-ink-950 p-6 lg:p-8" data-id="element-1705" data-mp="topcccet6e40">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-1706" data-mp="a12zcbahrv2g">
                    The complete system
                  </p>
                  <p className="grit-headline mt-3 text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold uppercase text-offwhite" data-id="element-1707" data-mp="j1rej9bmg1gx">
                    Still want yours?
                    <br data-id="element-1708" data-mp="szp2dvpkjw90" />
                    <span className="text-grit-500" data-id="element-1709" data-mp="sz5mslne5jy5">Pick your pack.</span>
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1710" data-mp="o142xyi55glm">
                    Build your X1 from $29.95, or get the Complete System: 3 lenses, charging cable, hard case AV8, pouch,
                    cleaning cloth and Quick Start Guide, with the Lifetime Replacement Warranty free on every multi-pack.
                  </p>
                  <Link to="/#offer" className="mt-6 inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-1711" data-mp="1bqg6oiup8wgj">
                    Back to the packs
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1712" data-mp="i52f1rkfasgp" />
                  </Link>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-steel-400" data-id="element-1713" data-mp="n45mfinvlaru">
                    See current packs and pricing
                  </p>
                </div>
              </div>

              {doc.sections.some(section => section.fine) && <div className="mt-10 grid gap-8" data-id="element-1714" data-mp="x08lugrcevb3">
                  {doc.sections.filter(section => section.fine).map(renderSection)}
                </div>}

              <div className="mt-8 flex flex-col gap-4 border border-white/12 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1715" data-mp="2yv13i1zii05e">
                <p data-id="element-1716" data-mp="2vbs9bvlxigog">
                  <span className="block font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-1717" data-mp="2vcburmasfn78">
                    Still have a question?
                  </span>
                  <a href="mailto:support@gritvuegear.com" className="mt-2 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1718" data-mp="2llpkm7lubovx">
                    <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1719" data-mp="39bjwalufb7el" />
                    support@gritvuegear.com
                  </a>
                </p>
                <Link to="/#offer" className="inline-flex items-center justify-center bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px]" data-id="element-1720" data-mp="358530thh4uj7">
                  Shop the X1
                </Link>
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1721" data-mp="3szksp4ekgay1">
              <nav aria-label="Policies" className="border border-white/12 bg-ink-900" data-id="element-1722" data-mp="3t0vzze7800tw">
                <p className="border-b border-white/10 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1723" data-mp="3f1cs8mibmri4">
                  Policies
                </p>
                <ul data-id="element-1724" data-mp="bk4d4aeza5r3">
                  {legalDocs.map(item => <li key={item.slug} className="border-b border-white/5 last:border-b-0" data-id="element-1725" data-mp="ghwerw5fd68h">
                      <Link to={`/legal/${item.slug}`} className={`block px-5 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-150 ease-grit ${item.slug === doc.slug ? 'bg-grit-500/10 text-grit-500' : 'text-steel-300 hover:text-offwhite'}`} data-id="element-1726" data-mp="1tjfoqb1j79r">
                        {item.title}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <ul className="mt-6 grid gap-3 border border-white/12 bg-ink-900 p-5" data-id="element-1727" data-mp="2srw7exi86a1v">
                {promises.map(promise => {
              const Icon = promise.icon;
              return <li key={promise.label} className="flex items-start gap-3 text-[14px] font-medium text-steel-200" data-id="element-1728" data-mp="20zf16all6sib">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1729" data-mp="1w2f60uq9za5z" />
                      {promise.label}
                    </li>;
            })}
              </ul>

              <div className="mt-6" data-id="element-1730" data-mp="2508s7cubsnff">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-steel-500" data-id="element-1731" data-mp="3lg1xlzs1sr93">Secure checkout</p>
                <div className="mt-2" data-id="element-1732" data-mp="3ghgunslevtgy">
                  <PaymentLogos data-id="element-1733" data-mp="3ggfg3e56souh" />
                </div>
              </div>
            </aside>
          </div>}
      </main>

      <SiteFooter data-id="element-1734" data-mp="17c20hbqz7ozq" />
    </div>;
}